<template>
  <div class="ingredient-chart-wrap">
    <div class="ingredient-chart" :class="chartClasses">
      <img :src="baseChartImage" class="ingredient-chart__image" loading="lazy" />

      <div class="ingredient-chart__ingredients-images ingredients-images">
        <div
          v-for="{
            name,
            tooltipContent,
            imageWrapperStyle,
            imageWrapperClass,
            imageSrc
          } of ingredientsImageConfigs"
          :key="name"
          :data-title="tooltipContent"
          :style="imageWrapperStyle"
          :class="imageWrapperClass"
          class="ingredients-images__image-wrapper image-wrapper"
        >
          <img
            class="image-wrapper__image"
            :src="imageSrc"
            :alt="name"
            loading="lazy"
            @click="$emit('ingredient-click', name)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HexagonImage from '@/assets/images/ingredients-section/hexagon.svg';
import PentagonImage from '@/assets/images/ingredients-section/pentagon.svg';
import SquareImage from '@/assets/images/ingredients-section/square.svg';
import TriangleImage from '@/assets/images/ingredients-section/triangle.svg';
import CirclesImage from '@/assets/images/ingredients-section/circles.svg';
import CircleImage from '@/assets/images/ingredients-section/circle.svg';

import { getIngredientName, getIngredientTotalConcentration } from '@/models/core/Ingredient';

import { INGREDIENT_NAME_KEY } from '@/constants/ingredients';

const INGREDIENTS_CHART_BASE_IMAGES = {
  circle: CircleImage,
  circles: CirclesImage,
  triangle: TriangleImage,
  square: SquareImage,
  pentagon: PentagonImage,
  hexagon: HexagonImage
};

const TOOLTIP_PLACEMENT = {
  circle: {
    0: 'top'
  },
  circles: {
    0: 'top',
    1: 'top'
  },
  triangle: {
    0: 'top',
    1: 'right',
    2: 'left'
  },
  square: {
    0: 'top',
    1: 'right',
    2: 'bottom',
    3: 'left'
  },
  pentagon: {
    0: 'top',
    1: 'right',
    2: 'right',
    3: 'left',
    4: 'left'
  },
  hexagon: {
    0: 'top',
    1: 'right',
    2: 'right',
    3: 'bottom',
    4: 'left',
    5: 'left'
  }
};

const INGREDIENTS_IMAGES_STYLES = {
  circle: {
    0: {
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)'
    }
  },
  circles: {
    0: {
      left: '30%',
      top: '38%',
      transform: 'translate(-50%, -50%)'
    },
    1: {
      right: '30%',
      top: '38%',
      transform: 'translate(50%, -50%)'
    }
  },
  triangle: {
    0: {
      left: '50%',
      top: '0',
      transform: 'translateX(-50%)'
    },
    1: {
      right: '0',
      bottom: '0'
    },
    2: {
      left: '0',
      bottom: '0'
    }
  },
  square: {
    0: {
      left: '50%',
      top: '0',
      transform: 'translateX(-50%)'
    },
    1: {
      right: '0',
      top: '50%',
      transform: 'translateY(-50%)'
    },
    2: {
      left: '50%',
      bottom: '0',
      transform: 'translateX(-50%)'
    },
    3: {
      left: '0',
      top: '50%',
      transform: 'translateY(-50%)'
    }
  },
  pentagon: {
    0: {
      left: '50%',
      top: '0',
      transform: 'translateX(-50%)'
    },
    1: {
      right: '0',
      top: '40%',
      transform: 'translateY(-50%)'
    },
    2: {
      right: '28%',
      bottom: '0',
      transform: 'translateX(50%)'
    },
    3: {
      left: '28%',
      bottom: '0',
      transform: 'translateX(-50%)'
    },
    4: {
      left: '0',
      top: '40%',
      transform: 'translateY(-50%)'
    }
  },
  hexagon: {
    0: {
      left: '37%',
      top: '0'
    },
    1: {
      right: '0',
      top: '18%'
    },
    2: {
      right: '0',
      bottom: '18%'
    },
    3: {
      left: '37%',
      bottom: '0'
    },
    4: {
      left: '0',
      bottom: '18%'
    },
    5: {
      left: '0',
      top: '18%'
    }
  }
};

export default {
  name: 'YourIngredientsChart',
  props: {
    ingredients: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      ingredientsImageConfigs: []
    };
  },
  computed: {
    baseChartImageName() {
      const baseImage = Object.keys(INGREDIENTS_CHART_BASE_IMAGES)[this.ingredients.length - 1];

      return baseImage;
    },
    baseChartImage() {
      return INGREDIENTS_CHART_BASE_IMAGES[this.baseChartImageName];
    },
    isOneIngredientSerum() {
      return this.ingredients.length === 1;
    },
    isTwoIngredientsSerum() {
      return this.ingredients.length === 2;
    },
    chartClasses() {
      return {
        'ingredient-chart--one': this.ingredients.length === 1,
        'ingredient-chart--two': this.ingredients.length === 2,
        'ingredient-chart--three': this.ingredients.length === 3,
        'ingredient-chart--four': this.ingredients.length === 4,
        'ingredient-chart--five': this.ingredients.length === 5,
        'ingredient-chart--six': this.ingredients.length === 6
      };
    }
  },
  async created() {
    this.ingredientsImageConfigs = await Promise.all(
      this.ingredients.map(async (ingredient, index) => {
        const ingredientName = getIngredientName(ingredient);
        const concentration = getIngredientTotalConcentration(ingredient);

        const imageSrc = (await import(`@/assets/images/ingredients-images/${ingredientName}.jpg`))
          .default;
        const ingredientDisplayName = this.$t(INGREDIENT_NAME_KEY[ingredientName]);

        return {
          name: ingredientName,
          tooltipContent: `${ingredientDisplayName} ${concentration.toFixed(1)}%`,
          imageSrc,
          imageWrapperStyle: {
            ...INGREDIENTS_IMAGES_STYLES[this.baseChartImageName][index]
          },
          tooltipPlacement: TOOLTIP_PLACEMENT[this.baseChartImageName][index],
          imageWrapperClass: {
            'ingredients-images__image-wrapper--one': this.isOneIngredientSerum,
            'ingredients-images__image-wrapper--two': this.isTwoIngredientsSerum
          }
        };
      })
    );
  }
};
</script>

<style lang="scss" scoped>
.ingredient-chart {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  --ingredient-image-size: 24%;

  &--one,
  &--two {
    padding: 0;
  }

  &--three,
  &--four,
  &--five,
  &--six {
    padding: calc(var(--ingredient-image-size) * 0.4);
  }

  &__image {
    width: 100%;
  }

  &__ingredients-images {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}

.ingredients-images {
  &__image-wrapper {
    position: absolute;
    max-width: var(--ingredient-image-size);
    width: 100%;
    height: var(--ingredient-image-size);
    cursor: pointer;

    &--one {
      --ingredient-image-size: 80%;
    }

    &--two {
      --ingredient-image-size: 38%;
    }
  }
}

.image-wrapper {
  &__image {
    width: inherit;
    border-radius: 50%;
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
    cursor: pointer;
  }
}

@media (min-width: 960px) {
  .image-wrapper {
    &:after {
      display: none;
    }

    &:hover::after {
      position: absolute;
      display: block;
      width: max-content;
      max-width: 200px;
      padding: 12px;
      margin-top: 10px;
      color: var(--u-color-grey-700);
      background-color: var(--u-color-grey-100);
      font-family: var(--u-font-family-Helvetica);
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      letter-spacing: 0.4px;
      border-radius: 10px;
      content: attr(data-title);
    }
  }
}
</style>
